import type { ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { readWebSurveyPhoneVerified } from '@/features/auth/lib/constants'
import {
  InstrumentLinkConsentView,
  InstrumentLinkErrorView,
  InstrumentLinkInvalidView,
  InstrumentLinkLoadingView,
  InstrumentLinkVerifyView,
} from '../components/InstrumentLinkOnboardingViews'
import { useInstrumentLinkOnboarding } from '../hooks/useInstrumentLinkOnboarding'
import {
  ONBOARDING_CONSENT_HEADING,
  ONBOARDING_OTP_HEADING,
  ONBOARDING_PHONE_HEADING,
} from '../lib/constants'
import type { InstrumentOnboardingStep, SpOnboardingChannel } from '../lib/types'
import { SignupShareThemeProvider } from '../styles/signupShareTheme'

function onboardingDocumentTitle(
  step: InstrumentOnboardingStep,
  channel: SpOnboardingChannel,
): string {
  if (step === 'privacy' || step === 'terms' || step === 'declined') {
    return ONBOARDING_CONSENT_HEADING
  }
  if (step === 'otp') return ONBOARDING_OTP_HEADING
  if (channel === 'sms') return ONBOARDING_PHONE_HEADING
  return ONBOARDING_OTP_HEADING
}

export function InstrumentLinkOnboardingPage({
  linkTokenOverride,
  preferSms = false,
  signupShareFlow = false,
}: Readonly<{
  linkTokenOverride?: string | null
  preferSms?: boolean
  signupShareFlow?: boolean
}>) {
  const vm = useInstrumentLinkOnboarding(
    linkTokenOverride === undefined && !preferSms && !signupShareFlow
      ? undefined
      : { linkTokenOverride, preferSms, signupShareFlow },
  )
  useDocumentTitle(onboardingDocumentTitle(vm.step, vm.channel))
  return renderInstrumentOnboardingBody(vm, signupShareFlow)
}

function renderInstrumentOnboardingBody(
  vm: ReturnType<typeof useInstrumentLinkOnboarding>,
  signupShareFlow: boolean,
): ReactElement {
  if (!vm.linkToken && !vm.surveyFirstPhone) {
    return <InstrumentLinkInvalidView />
  }

  if (vm.surveyFirstPhone && readWebSurveyPhoneVerified()) {
    return <Navigate to="/instrument/onboarding/account" replace />
  }

  if (!vm.session && vm.serverError) {
    return <InstrumentLinkErrorView message={vm.serverError} />
  }

  const waitingForLinkStart =
    !vm.surveyFirstPhone &&
    (vm.step === 'loading' || (vm.linkStartPending && !vm.session))
  if (waitingForLinkStart) {
    return <InstrumentLinkLoadingView />
  }

  if (vm.step === 'privacy' || vm.step === 'terms') {
    return wrapSignupTheme(signupShareFlow, <InstrumentLinkConsentView {...consentViewProps(vm)} />)
  }

  return wrapSignupTheme(
    signupShareFlow || vm.surveyFirstPhone,
    <InstrumentLinkVerifyView vm={vm} />,
  )
}

function consentViewProps(vm: ReturnType<typeof useInstrumentLinkOnboarding>) {
  const isPrivacy = vm.step === 'privacy'
  return {
    step: isPrivacy ? ('privacy' as const) : ('terms' as const),
    serverError: vm.serverError,
    acceptPending: isPrivacy
      ? vm.acceptPrivacy.isPending
      : vm.acceptPrivacy.isPending || vm.acceptTerms.isPending,
    showConsentDeclined: vm.showConsentDeclined,
    onAccept: isPrivacy ? vm.handleAcceptPrivacy : vm.handleAcceptTerms,
    onDecline: () => vm.handleDeclineConsent(isPrivacy ? 'privacy' : 'terms'),
    onRetryConsent: vm.handleRetryConsent,
  }
}

function wrapSignupTheme(enabled: boolean, node: ReactElement) {
  return enabled ? <SignupShareThemeProvider>{node}</SignupShareThemeProvider> : node
}
